import { createApiProvider, createEndpointHooks } from '@croissants/ui';
import { useLocale } from '../i18n/index';
import { useSession } from './auth';
import { config } from './env';
import { describeApiError } from './error-sentence';

/**
 * The typed API client, wired to the session - built ONCE per provider mount by
 * `createApiProvider`, which reads the current session through a ref on every
 * request, so a Clerk token refresh or a sign-out lands on the next call
 * instead of rebuilding the client under every cached query.
 *
 * Contracts are the single source of truth for every wire shape: one zod schema
 * is the server's validation and this client's inferred type, with no codegen
 * step (tech-stack section 2). The base URL is injected from configuration - no
 * host is hardcoded anywhere in this portal (PRD OPS-018). The pilot session is
 * a cookie; Clerk and dev stay headers (F6).
 *
 * Hono stays confined to `apps/api-worker` (tech-stack section 2), so the typing
 * here comes from the endpoint descriptors rather than from `hono/client`, and
 * this app stays inside its dependency budget (tech-stack section 11).
 */
const provider = createApiProvider({
  baseUrl: config.apiBaseUrl,
  pilotCookie: true,
  useHeaders: () => {
    const session = useSession();
    return () => session.authHeaders();
  },
});

export const ApiProvider = provider.ApiProvider;
export const useApi = provider.useApi;

// ---------------------------------------------------------------------------
// The descriptor-driven layer
// ---------------------------------------------------------------------------

/**
 * Reads, writes, keys and invalidation, bound to this portal's client.
 *
 * The layer itself lives in `@croissants/ui` because it is not this portal's -
 * it was written here, and the other three portals then hand-rolled the same
 * thing four different ways, which is exactly what made
 * `scripts/check-write-refetch.mjs` blind to three of them. Nothing about it
 * knows how a portal signs in; `useApi().client` is the whole of what it needs.
 */
const hooks = createEndpointHooks(() => useApi().client);

export const useInvalidate = hooks.useInvalidate;
export const useEndpointQuery = hooks.useEndpointQuery;
export const useEndpointPages = hooks.useEndpointPages;
export const useEndpointAll = hooks.useEndpointAll;
export const useEndpointMutation = hooks.useEndpointMutation;

// ---------------------------------------------------------------------------
// Failure messages
// ---------------------------------------------------------------------------

/**
 * The console's failure sentence, in `./error-sentence` so it is testable
 * without a client: the platform's one renderer, plus the refusals whose
 * sentence only the server can write.
 */
export function useApiErrorMessage(): (error: unknown) => string {
  const { locale } = useLocale();
  return (error: unknown) => describeApiError(error, locale);
}
